import React, {ElementType, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {ColorPath} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {getCssColorByPath} from '../../../utils/colors'

export type TitleVariant =
  | 'titleXS'
  | 'titleS'
  | 'titleM'
  | 'titleL'
  | 'titleXL'
  | 'title2XL'
  | 'title3XL'
  | 'title4XL'
  | 'title5XL'

export type BodyVariant = 'bodyXS' | 'bodyS' | 'bodyM' | 'bodyL'

export type LabelVariant = 'labelXS' | 'labelS' | 'labelM'

export type TextVariant = BodyVariant | LabelVariant | TitleVariant

export type TextAlign =
  | 'inherit'
  | 'center'
  | 'end'
  | 'start'
  | 'justify'
  | 'left'
  | 'right'

interface TextProps {
  /** HTML Element that text will be rendered */
  /** @deprecated Linaria doesn't support `as` prop usage for the styled component. Use `tag` prop instead*/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: ElementType<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag?: ElementType<any>
  /** Specify the color path from the theme's colors file to explicitly set the Text's color */
  colorPath?: ColorPath
  /** The text variant style from the theme */
  variant?: TextVariant
  /** HTML class to be included in the tag */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Text to be displayed */
  children: ReactNode
  /** text-align style */
  align?: TextAlign
  id?: string
  style?: React.CSSProperties
  /** Allow disabling of in-browser translation */
  translate?: 'no'
  /** Text direction; use "auto" for content-derived strings whose language may not match the page direction */
  dir?: 'auto' | 'ltr' | 'rtl'
}

interface StyleProps {
  align: TextAlign
  colorValue?: string
}

/** We don't want to change Text component interface for developers, so we should still accept all possible values, but convert them into the proper ones */
const textAlignMap = {
  inherit: 'inherit',
  center: 'center',
  end: 'end',
  start: 'start',
  justify: 'justify',
  left: 'start',
  right: 'end'
}

const TextElement = styled.p<StyleProps>`
  margin: 0;
  text-align: var(--align, inherit);
  color: var(--color, inherit);

  &.--typography-bodyXS {
    ${cssTheme.typography.bodyXS}
  }
  &.--typography-bodyS {
    ${cssTheme.typography.bodyS}
  }
  &.--typography-bodyM {
    ${cssTheme.typography.bodyM}
  }
  &.--typography-bodyL {
    ${cssTheme.typography.bodyL}
  }
  &.--typography-labelXS {
    ${cssTheme.typography.labelXS}
  }
  &.--typography-labelS {
    ${cssTheme.typography.labelS}
  }
  &.--typography-labelM {
    ${cssTheme.typography.labelM}
  }
  &.--typography-titleXS {
    ${cssTheme.typography.titleXS}
  }
  &.--typography-titleS {
    ${cssTheme.typography.titleS}
  }
  &.--typography-titleM {
    ${cssTheme.typography.titleM}
  }
  &.--typography-titleL {
    ${cssTheme.typography.titleL}
  }
  &.--typography-titleXL {
    ${cssTheme.typography.titleXL}
  }
  &.--typography-title2XL {
    ${cssTheme.typography.title2XL}
  }
  &.--typography-title3XL {
    ${cssTheme.typography.title3XL}
  }
  &.--typography-title4XL {
    ${cssTheme.typography.title4XL}
  }
  &.--typography-title5XL {
    ${cssTheme.typography.title5XL}
  }
`

const getTagName = (variant: TextVariant) => {
  switch (variant) {
    case 'titleXS':
      return 'h6'
    case 'titleS':
      return 'h5'
    case 'titleM':
      return 'h4'
    case 'titleL':
      return 'h3'
    case 'titleXL':
      return 'h2'
    case 'title2XL':
    case 'title3XL':
    case 'title4XL':
    case 'title5XL':
      return 'h1'

    default:
      return 'p'
  }
}

export const Text = ({
  as,
  tag,
  colorPath,
  variant = 'bodyM',
  className,
  dataId,
  align = 'inherit',
  children,
  id,
  style,
  translate,
  dir
}: TextProps) => {
  const colorValue = colorPath && getCssColorByPath(colorPath)
  const tagName = tag || as || getTagName(variant)

  return (
    <TextElement
      className={cx(className, `--typography-${variant}`)}
      style={{
        // @ts-expect-error: CSS variables are not typed
        ['--color']: colorValue,
        ['--align']: textAlignMap[align],
        ...style
      }}
      as={tagName}
      colorValue={colorValue}
      data-id={dataId}
      align={align}
      id={id}
      translate={translate}
      dir={dir}
    >
      {children}
    </TextElement>
  )
}
